import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router'
import { api } from '../api/client'
import type { SymbolFile } from '../api/crashes'
import { formatDateTime } from '../lib/format'

const size = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(2)} MiB`

/** The uploaded dSYMs, grouped by app version and build, newest first, and removing them. */
export function CrashSymbolsScreen() {
	const client = useQueryClient()
	const symbols = useQuery({
		queryKey: ['admin-symbols'],
		queryFn: () => api.get<{ symbols: SymbolFile[] }>('/api/v1/admin/symbols'),
	})
	const remove = useMutation({
		mutationFn: (files: SymbolFile[]) =>
			api.post<{ removed: number }>('/api/v1/admin/symbols/delete', {
				selection: files.map((f) => ({ uuid: f.binaryUuid, revision: f.revision })),
			}),
		onSettled: () => client.invalidateQueries({ queryKey: ['admin-symbols'] }),
	})
	const sets = new Map<string, SymbolFile[]>()
	for (const file of symbols.data?.symbols ?? []) {
		const key = JSON.stringify([file.bundleId, file.appVersion, file.appBuild])
		sets.set(key, [...(sets.get(key) ?? []), file])
	}
	const confirmRemove = (files: SymbolFile[]) =>
		window.confirm(
			`Remove ${files.length} symbol file${files.length === 1 ? '' : 's'}? Existing reports keep their stacks; new ones stay unsymbolicated until they are uploaded again.`,
		) && remove.mutate(files)

	return (
		<div className="stack">
			<div className="page-header">
				<div>
					<Link to="/admin?tab=crashes" className="small">
						← Crashes and Errors
					</Link>
					<h1 style={{ margin: '0.2rem 0 0' }}>Crash symbols</h1>
					{symbols.data && (
						<div className="muted small">
							{symbols.data.symbols.length} files in {sets.size} version/build sets ·{' '}
							{size(symbols.data.symbols.reduce((sum, f) => sum + f.byteSize, 0))} expanded
						</div>
					)}
				</div>
			</div>
			{symbols.isError && <p className="error">{symbols.error.message}</p>}
			{remove.isError && <p className="error">{remove.error.message}: reload and review the current files.</p>}
			{[...sets.values()].map((files) => (
				<section key={files[0]!.binaryUuid} className="card stack">
					<div className="row" style={{ justifyContent: 'space-between' }}>
						<h2 style={{ margin: 0 }}>
							{files[0]!.bundleId} {files[0]!.appVersion ?? 'Unknown'} ({files[0]!.appBuild ?? 'Unknown'})
						</h2>
						<button type="button" className="danger" onClick={() => confirmRemove(files)}>
							Remove set
						</button>
					</div>
					<div className="table-scroll">
						<table>
							<thead>
								<tr>
									<th>Binary</th>
									<th>Arch</th>
									<th>UUID</th>
									<th className="num">Expanded</th>
									<th>Uploaded</th>
									<th />
								</tr>
							</thead>
							<tbody>
								{files.map((f) => (
									<tr key={f.binaryUuid}>
										<td>{f.binaryName}</td>
										<td>{f.arch}</td>
										<td className="mono small">{f.binaryUuid}</td>
										<td className="num">{size(f.byteSize)}</td>
										<td className="small">{formatDateTime(f.uploadedAt)}</td>
										<td>
											<button type="button" className="danger" onClick={() => confirmRemove([f])}>
												Remove
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</section>
			))}
			{symbols.data && !symbols.data.symbols.length && <p className="muted">No dSYMs have been uploaded yet.</p>}
		</div>
	)
}
